import React from 'react';
import styled from 'styled-components';
import { useLocalStorageState } from 'ahooks';
import {
  Layout,
  Row,
  Col,
  Flex,
  Typography,
  Card,
  Switch,
  Button,
  Divider,
  List,
  Empty,
} from 'antd';
import { BETA_TOGGLES, SOFT_ROLLBACK_TOGGLES } from '../toggles';

const { Content } = Layout;
const { Title, Paragraph, Text } = Typography;

export const StyledWrapper = styled(Layout)`
  overflow: hidden;
  position: relative;
  height: max-content;
  min-height: 100%;
`;

export const StyledContent = styled(Content)`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  margin-bottom: 2rem;
`;

export const StyledFeatureCaption = styled(Flex)`
  padding: 2.4rem 0;
`;

// Re-exported from ../toggles so existing `from '.../screens/Beta'` imports keep
// working. New non-React consumers should import from '../toggles'.
export { BETA_TOGGLES, SOFT_ROLLBACK_TOGGLES };

export function Beta() {
  const [showBetaNotifications, setShowBetaNotifications] =
    useLocalStorageState<boolean>(BETA_TOGGLES.notifications.key, {
      defaultValue: false,
      listenStorageChange: true,
    });

  const [
    showBetaSmartOnboardingAIAssistant,
    setShowBetaSmartOnboardingAIAssistant,
  ] = useLocalStorageState<boolean>(
    BETA_TOGGLES.smartOnboardingAIAssistant.key,
    {
      defaultValue: false,
      listenStorageChange: true,
    }
  );

  const [showNewServiceCourseFlowEnabled, setShowNewServiceCourseFlowEnabled] =
    useLocalStorageState<boolean>(BETA_TOGGLES.newServiceCourseFlow.key, {
      defaultValue: false,
      listenStorageChange: true,
    });

  const [showGravityBasedAppsEnabled, setShowGravityBasedAppsEnabled] =
    useLocalStorageState<boolean>(BETA_TOGGLES.gravityBasedApps.key, {
      defaultValue: false,
      listenStorageChange: true,
    });

  const [showCalendarV2Enabled, setShowCalendarV2Enabled] =
    useLocalStorageState<boolean>(BETA_TOGGLES.calendarV2.key, {
      defaultValue: false,
      listenStorageChange: true,
    });

  const [showBookingsUpgradeEnabled, setShowBookingsUpgradeEnabled] =
    useLocalStorageState<boolean>(BETA_TOGGLES.bookingsUpgrade.key, {
      defaultValue: false,
      listenStorageChange: true,
    });

  const [showSoftRollbackMainMenu, setShowSoftRollbackMainMenu] =
    useLocalStorageState<boolean>(SOFT_ROLLBACK_TOGGLES.mainMenu.key, {
      defaultValue: false,
      listenStorageChange: true,
    });

  const handleOnBetaNotificationsToggleChange = (checked: boolean) => {
    setShowBetaNotifications(checked);
    if (!checked) {
      setShowBetaSmartOnboardingAIAssistant(false);
      setShowGravityBasedAppsEnabled(false);
      setShowCalendarV2Enabled(false);
      setShowBookingsUpgradeEnabled(false);
    }
  };

  const handleOnBetaSmartOnboardingAIAssistantToggleChange = (
    checked: boolean
  ) => {
    setShowBetaSmartOnboardingAIAssistant(checked);
  };

  const handleOnBetaNewServiceCourseFlowToggleChange = (checked: boolean) => {
    setShowNewServiceCourseFlowEnabled(checked);
  };

  const handleOnBetaCalendarV2ToggleChange = (checked: boolean) => {
    setShowCalendarV2Enabled(checked);
  };

  const handleOnBookingsUpgradeToggleChange = (checked: boolean) => {
    setShowBookingsUpgradeEnabled(checked);
  };

  const handleOnSoftRollbackMainMenuToggleChange = (checked: boolean) => {
    setShowSoftRollbackMainMenu(checked);
  };

  const handleOnGravityBasedAppsToggleChange = (checked: boolean) => {
    setShowGravityBasedAppsEnabled(checked);
  };

  return (
    <StyledWrapper>
      <StyledContent>
        <Row>
          <Col span={24}>
            <StyledFeatureCaption justify="flex-start" vertical>
              <Title level={3} style={{ fontWeight: 400, margin: 0 }}>
                Beta Features
              </Title>
              <Paragraph type="secondary">
                This page offers access to experimental features in beta
                testing, giving Beta Testing Program participants early access
                to new features before their official release.
              </Paragraph>
            </StyledFeatureCaption>
          </Col>
          <Col span={24}>
            <Card>
              <Row justify="space-between" align="middle" gutter={[16, 16]}>
                <Col md={16} sm={24} xs={24}>
                  <Title level={5} style={{ marginTop: 0 }}>
                    Beta Access Notifications
                  </Title>
                  <Paragraph type="secondary">
                    Enable this option to receive notifications whenever beta
                    features become available in specific parts of the
                    application. Once enabled, you’ll start seeing in-app alerts
                    when new beta features are ready for testing.
                  </Paragraph>
                </Col>
                <Col md={8} sm={24} xs={24}>
                  <Flex gap={16} align="end" vertical>
                    <Flex align="center" gap={16}>
                      <Text>Show Beta Notifications</Text>
                      <Switch
                        checkedChildren="On"
                        unCheckedChildren="Off"
                        checked={showBetaNotifications}
                        onChange={handleOnBetaNotificationsToggleChange}
                      />
                    </Flex>
                  </Flex>
                </Col>
              </Row>
              <Row
                align="middle"
                gutter={[16, 16]}
                justify="space-between"
                style={{ marginTop: 16 }}
              >
                <Col md={16} sm={24} xs={24}>
                  <Title level={5} style={{ marginTop: 0 }}>
                    Share Your Feedback
                  </Title>
                  <Paragraph type="secondary">
                    Your feedback helps us improve beta features before their
                    official release. Let us know what you think!
                  </Paragraph>
                </Col>
                <Col md={8} sm={24} xs={24}>
                  <Flex gap={16} align="end" vertical>
                    <Button type="primary" disabled>
                      Share Feedback
                    </Button>
                  </Flex>
                </Col>
              </Row>
              {showBetaNotifications && (
                <>
                  <Row align="middle">
                    <Col span={24}>
                      <Divider style={{ margin: '16px 0' }} />
                    </Col>
                  </Row>
                  <Row
                    align="middle"
                    gutter={[16, 16]}
                    justify="space-between"
                    style={{ marginTop: 16 }}
                  >
                    <Col md={16} sm={24} xs={24}>
                      <Title level={5} style={{ marginTop: 0 }}>
                        Beta Features Available for Testing
                      </Title>
                      <Paragraph type="secondary">
                        Explore the latest beta features available for testing.
                        Help us improve these features by trying them out and
                        providing feedback.
                      </Paragraph>
                    </Col>
                    <Col md={24} sm={24} xs={24}>
                      <List bordered>
                        <List.Item hidden key="empty_list">
                          <Empty
                            description="No beta features available for testing."
                            style={{ width: '100%' }}
                          />
                        </List.Item>
                        <List.Item
                          key={BETA_TOGGLES.smartOnboardingAIAssistant.key}
                          actions={[
                            <Flex align="center" gap={16}>
                              <Text>Try It</Text>
                              <Switch
                                checkedChildren="On"
                                unCheckedChildren="Off"
                                checked={showBetaSmartOnboardingAIAssistant}
                                onChange={
                                  handleOnBetaSmartOnboardingAIAssistantToggleChange
                                }
                              />
                            </Flex>,
                          ]}
                        >
                          <Flex vertical>
                            <Text strong>
                              Smart Onboarding with AI Assistant
                            </Text>
                            <Paragraph type="secondary">
                              This conversational experience helps users
                              complete onboarding steps faster and more
                              effectively through personalized guidance, timely
                              support, and clear next actions.
                            </Paragraph>
                          </Flex>
                        </List.Item>
                        <List.Item
                          key={BETA_TOGGLES.newServiceCourseFlow.key}
                          actions={[
                            <Flex align="center" gap={16}>
                              <Text>Try It</Text>
                              <Switch
                                checkedChildren="On"
                                unCheckedChildren="Off"
                                checked={showNewServiceCourseFlowEnabled}
                                onChange={
                                  handleOnBetaNewServiceCourseFlowToggleChange
                                }
                              />
                            </Flex>,
                          ]}
                        >
                          <Flex vertical>
                            <Text strong>DSP Service/Course V2 Flow</Text>
                            <Paragraph type="secondary">
                              This new service/course flow helps users create
                              services faster and more effectively through a
                              streamlined process combining 1-1 service and
                              1-many service(course) interactions, timely
                              guidance, and clear steps. The updated flow also
                              improves the experience of viewing appointment
                              course details and managing appointment course
                              settings.
                            </Paragraph>
                          </Flex>
                        </List.Item>
                        <List.Item
                          key={BETA_TOGGLES.gravityBasedApps.key}
                          actions={[
                            <Flex align="center" gap={16}>
                              <Text>Try It</Text>
                              <Switch
                                checkedChildren="On"
                                unCheckedChildren="Off"
                                checked={showGravityBasedAppsEnabled}
                                onChange={handleOnGravityBasedAppsToggleChange}
                              />
                            </Flex>,
                          ]}
                        >
                          <Flex vertical>
                            <Text strong>Show Gravity-based Apps</Text>
                            <Paragraph type="secondary">
                              Enable internal teams to test apps migrated to
                              Gravity. When enabled, migrated apps will show in
                              Navigation. Check with your teammates which apps
                              are ready to test.
                            </Paragraph>
                          </Flex>
                        </List.Item>
                        <List.Item
                          key={BETA_TOGGLES.calendarV2.key}
                          actions={[
                            <Flex align="center" gap={16}>
                              <Text>Try It</Text>
                              <Switch
                                checkedChildren="On"
                                unCheckedChildren="Off"
                                checked={showCalendarV2Enabled}
                                onChange={handleOnBetaCalendarV2ToggleChange}
                              />
                            </Flex>,
                          ]}
                        >
                          <Flex vertical>
                            <Text strong>Show new calendar v2 menu item</Text>
                            <Paragraph type="secondary">
                              When enabled, a separate “Calendar v2” item
                              appears in the side menu next to the existing
                              Calendar item, letting you preview the new
                              calendar interface side-by-side with the current
                              one.
                            </Paragraph>
                          </Flex>
                        </List.Item>
                        <List.Item
                          key={BETA_TOGGLES.bookingsUpgrade.key}
                          actions={[
                            <Flex align="center" gap={16}>
                              <Text>Try It</Text>
                              <Switch
                                checkedChildren="On"
                                unCheckedChildren="Off"
                                checked={showBookingsUpgradeEnabled}
                                onChange={handleOnBookingsUpgradeToggleChange}
                              />
                            </Flex>,
                          ]}
                        >
                          <Flex vertical>
                            <Text strong>Upgrade promo to Bookings</Text>
                            <Paragraph type="secondary">
                              For merchants on a capped monthly appointment
                              allowance, surfaces the upgrade path to the
                              Commerce plan: a card in the Bookings side menu, a
                              dialog when the allowance is nearly or fully used,
                              and a banner in the appointment form.
                            </Paragraph>
                          </Flex>
                        </List.Item>
                      </List>
                    </Col>
                  </Row>
                </>
              )}
              <>
                <Row align="middle" hidden>
                  <Col span={24}>
                    <Divider style={{ margin: '16px 0' }} />
                  </Col>
                </Row>
                {
                  // TODO: Thats an example of soft rollback toggle, we can add more toggles here in the future when needed
                  <Row
                    align="middle"
                    gutter={[16, 16]}
                    justify="space-between"
                    style={{ marginTop: 16, display: 'none' }}
                  >
                    <Col md={16} sm={24} xs={24}>
                      <Title level={5} style={{ marginTop: 0 }}>
                        Soft Rollback (Pre-Report Verification)
                      </Title>
                      <Paragraph type="secondary">
                        Before reporting an issue, use Soft Rollback to verify
                        whether the behavior is truly a bug or part of the new
                        rollout. This feature allows you to temporarily compare
                        the new version with the previous stable version
                        directly in your local browser. By validating the
                        behavior first, you help reduce false bug reports and
                        give the development team clearer, more actionable
                        feedback.
                      </Paragraph>
                    </Col>

                    <Col md={24} sm={24} xs={24}>
                      <List bordered>
                        <List.Item key="empty_list" hidden>
                          <Empty
                            description="No features available to try soft rollback."
                            style={{ width: '100%' }}
                          />
                        </List.Item>
                        <List.Item
                          hidden
                          key="soft_rollback_main_menu"
                          actions={[
                            <Flex align="center" gap={16}>
                              <Text>Try It</Text>
                              <Switch
                                checkedChildren="On"
                                unCheckedChildren="Off"
                                checked={showSoftRollbackMainMenu}
                                onChange={
                                  handleOnSoftRollbackMainMenuToggleChange
                                }
                              />
                            </Flex>,
                          ]}
                        >
                          <Flex vertical>
                            <Text strong>Main Menu & Top Header</Text>
                            <Paragraph type="secondary">
                              Not sure if it’s a bug? Use Soft Rollback to
                              temporarily switch back to the previous menu
                              version and confirm whether the issue is specific
                              to the new rollout.
                            </Paragraph>
                          </Flex>
                        </List.Item>
                      </List>
                    </Col>
                  </Row>
                }
              </>
            </Card>
          </Col>
        </Row>
      </StyledContent>
    </StyledWrapper>
  );
}
